import { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { securityApi } from '../services/securityApi';
import type {
  CanonicalEventFilters,
  CanonicalEventListResponse,
} from '../types/security';
import { usePollingResource } from './usePollingResource';

const DEFAULT_INTERVAL_MS = 30_000;

interface UseCanonicalEventsOptions {
  /** WQ-286 Item 4: short-circuit fetch when operator deselected all chips. */
  skip?: boolean;
}

export function useCanonicalEvents(
  filters: CanonicalEventFilters = {},
  optionsOrInterval: UseCanonicalEventsOptions | number = DEFAULT_INTERVAL_MS,
) {
  const { authFetch } = useAuth();
  const { skip, intervalMs } = useMemo(() => {
    if (typeof optionsOrInterval === 'number') {
      return { skip: false, intervalMs: optionsOrInterval };
    }
    return {
      skip: optionsOrInterval.skip ?? false,
      intervalMs: DEFAULT_INTERVAL_MS,
    };
  }, [optionsOrInterval]);

  // Serialize filters into a stable key so the polling effect restarts only
  // when filters actually change.
  const filterKey = useMemo(() => JSON.stringify(filters), [filters]);

  return usePollingResource<CanonicalEventListResponse>({
    fetcher: () => securityApi.listCanonicalEvents(authFetch, filters),
    intervalMs,
    deps: [filterKey],
    skip,
  });
}
